// ---------------------------------------------------------------
// AuthView.js — Inicio de sesión con:
//  · correo + contraseña
//  · nombre de usuario + contraseña (se resuelve a su correo
//    mediante la colección pública `usernames`)
//  · cuenta de Google
// El registro público sigue deshabilitado: las cuentas de correo
// las crea el administrador.
// ---------------------------------------------------------------
import { useState } from "react";
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "../firebase";
import {
  resolveIdentifierToEmail,
  sendPasswordSetupEmail,
} from "../services/authService";
import Footer from "./Footer";
import { maskEmail } from "../utils/maskEmail";

const ERROR_MESSAGES = {
  "auth/invalid-email": "El correo electrónico no es válido.",
  "auth/user-not-found": "No existe ninguna cuenta con estos datos.",
  "auth/wrong-password": "La contraseña es incorrecta.",
  "auth/invalid-credential":
    "Usuario o contraseña incorrectos. Si aún no tienes contraseña, pulsa «Establecer contraseña».",
  "auth/operation-not-allowed":
    "El inicio de sesión con correo y contraseña no está activo en este proyecto.",
  "auth/user-disabled": "Esta cuenta está deshabilitada.",
  "auth/too-many-requests": "Demasiados intentos. Prueba de nuevo en unos minutos.",
  "auth/network-request-failed": "Error de red. Comprueba tu conexión.",
  "auth/popup-closed-by-user": "Has cerrado la ventana de Google antes de terminar.",
  "auth/popup-blocked": "El navegador ha bloqueado la ventana emergente de Google.",
  "app/username-not-found": "No existe ningún usuario con ese nombre.",
};

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function AuthView() {
  const [identifier, setIdentifier] = useState(""); // correo o username
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const fail = (err) => {
    if (!ERROR_MESSAGES[err.code]) console.error(err);
    setInfo("");
    setError(ERROR_MESSAGES[err.code] || "Ha ocurrido un error. Inténtalo de nuevo.");
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const email = await resolveIdentifierToEmail(identifier);
      if (!email) throw Object.assign(new Error(), { code: "app/username-not-found" });
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      fail(err);
    }
  };

  const handleSetPassword = async () => {
    setError("");
    setInfo("");
    if (!identifier.trim()) {
      setError("Escribe tu correo o nombre de usuario para enviarte el enlace.");
      return;
    }
    setLoading(true);
    try {
      const email = await sendPasswordSetupEmail(identifier);
      // Nunca el correo entero: cualquiera podría teclear un usuario
      // ajeno y quedarse con su dirección.
      setInfo(
        `Te hemos enviado un correo a ${maskEmail(email)} para establecer tu contraseña. Cuando la configures, podrás entrar con correo o usuario.`
      );
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      fail(err);
    }
  };

  return (
    <div className="auth-screen">
      {/* Blobs de color desenfocados de fondo */}
      <div className="auth-blob auth-blob-1" />
      <div className="auth-blob auth-blob-2" />
      <div className="auth-blob auth-blob-3" />

      <div className="auth-card">
        <img
          src={`${process.env.PUBLIC_URL}/logo.png`}
          alt="Farmacia Buendía"
          className="auth-logo-img"
        />
        <h1 className="auth-title">Farmacia Buendía</h1>
        <p className="auth-subtitle">Accede a tu calendario laboral</p>

        <form className="auth-form" onSubmit={handleSubmit}>
          <input
            className="auth-input"
            type="text"
            placeholder="Correo o nombre de usuario"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoComplete="username"
            required
          />
          <input
            className="auth-input"
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          {error && <div className="auth-error">{error}</div>}
          {info && <div className="auth-info">{info}</div>}

          <button className="auth-submit" type="submit" disabled={loading}>
            {loading ? "Un momento…" : "Iniciar sesión"}
          </button>

          <button
            type="button"
            className="auth-link-btn"
            onClick={handleSetPassword}
            disabled={loading}
          >
            Establecer o recuperar contraseña
          </button>
        </form>

        <div className="auth-divider"><span>o</span></div>

        <button
          type="button"
          className="google-btn"
          onClick={handleGoogle}
          disabled={loading}
        >
          <GoogleIcon />
          Continuar con Google
        </button>

        <p className="auth-footnote">
          ¿No tienes cuenta? Pídesela al administrador.
        </p>
      </div>

      <Footer />
    </div>
  );
}
